import React, { useState, useEffect } from 'react';
import {
    MenuItem,
    Select,
    OutlinedInput,
    Chip,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Snackbar,
} from '@material-ui/core';
import _ from 'lodash';
import SRCards from '../../components/SRCards';
import UserApi from '../../services/user.service';
import { CHECK_VALUE } from '../../common/data-validator';
import { Role, Permission } from './interfaces/user.interface';
import { Alert } from '@material-ui/lab';

type SnackbarSeverity = 'success' | 'error' | 'info' | 'warning';
type SnackbarState = {
    open: boolean;
    message: string;
    severity: SnackbarSeverity;
};
const RoleList = () => {
    const [roles, setRoles] = useState<Role[]>([]);
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [features, setFeatures] = useState<Permission[]>([]); // features have _id and name
    const [selectedRole, setSelectedRole] = useState<Role>({} as Role);
    const [selectedPermissions, setSelectedPermissions] = useState<string[]>(
        []
    );
    const [search, setSearch] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [showRoleDialog, setShowRoleDialog] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [snackbar, setSnackbar] = useState<SnackbarState>({
        open: false,
        message: '',
        severity: 'success',
    });

    useEffect(() => {
        const page = 1;
        const limit = 100;
        Promise.all([
            UserApi.getPermissions('', limit, page),
            UserApi.getFeatures('', limit, page),
            UserApi.getRoles(search, limit, page),
        ])
            .then(([perms, feats, roles]) => {
                setPermissions(perms);
                setFeatures(feats);
                setRoles(roles);
            })
            .catch(()=> showSnackbar('Failed to fetch some data', 'error'))
            .finally(() => setIsLoading(false));
    }, []);

    const showSnackbar = (message: string, severity: SnackbarSeverity) => {
        setSnackbar({ open: true, message, severity });
    };

    const searchRoles = () => {
        setIsLoading(true);
        const page = 1;
        const limit = 100;
        UserApi.getRoles(search, limit, page)
            .then(setRoles)
            .catch(() => showSnackbar('Failed to search roles', 'error'))
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        searchRoles();
    }, [search]);

    const resetStates = () => {
        setSelectedRole({} as Role);
        setSelectedPermissions([]);
    };

    const handleAddClick = () => {
        resetStates();
        setIsEditMode(false);
        setShowRoleDialog(true);
    };

    const handleEditClick = (role: Role) => {
        const roleInfo = _.clone(role);
        const permissionNames = getPermissionNames(roleInfo.permissions || []);
        setSelectedRole(roleInfo);
        setSelectedPermissions(permissionNames);
        setIsEditMode(true);
        setShowRoleDialog(true);
    };

    const handleClose = () => {
        setShowRoleDialog(false);
        resetStates();
        setIsEditMode(false);
    };

    const getPermissionNames = (permissionIds: string[]) => {
        return permissions
            .filter((perm) => permissionIds.includes(perm._id!))
            .map((perm) => perm.name!);
    };

    const addPermissionIdsToRole = (role: Role) => {
        const permissionIds = permissions
            .filter((perm) => selectedPermissions.includes(perm.name!))
            .map((perm) => perm._id!)
            .filter(Boolean);
        role.permissions = Array.from(new Set(permissionIds));
    };

    const verifyRolePayload = (role: Role) => {
        const newErrors: { [key: string]: string } = {};
        if (!role.name || role.name.trim() === '') {
            newErrors.name = 'Role name is required';
        }
        if (!role.featureId || role.featureId.trim() === '') {
            newErrors.featureId = 'Feature is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setSelectedRole((prev) => ({ ...prev, [name]: value }));
    };

    const handlePermissionChange = (e: any) => {
        const value = e.target.value || [];
        setSelectedPermissions(value as string[]);
    };

    const handleFeatureChange = (e: any) => {
        const value = e.target.value;
        setSelectedRole((prev) => ({ ...prev, featureId: value }));
    };

    const handleSave = () => {
        const role = _.clone(selectedRole);
        if (!verifyRolePayload(role)) return;

        addPermissionIdsToRole(role);

        if (!isEditMode) {
            // Create new role
            UserApi.createRole(role)
                .then(() => {
                    showSnackbar('Successfully created the role', 'success');
                    searchRoles();
                })
                .catch(() => showSnackbar('Failed to create the role.', 'error'));
        } else {
            // Update role
            const roleId = role._id!;
            if (!CHECK_VALUE(roleId)) {
                console.error('Role ID cannot be empty');
                return;
            }
            UserApi.updateRole(roleId, role)
                .then(() => {
                    showSnackbar('Successfully updated the role.', 'success');
                    searchRoles();
                })
                .catch(() => showSnackbar('Failed to update role.', 'error'));
        }
        handleClose();
    };

    return (
        <>
            <SRCards />
            <Container style={{ marginTop: 40 }}>
                <Box
                    display='flex'
                    justifyContent='space-between'
                    alignItems='center'
                    mb={2}
                >
                    <Typography variant='h5'>Role Management</Typography>
                    <Button
                        variant='contained'
                        color='primary'
                        onClick={handleAddClick}
                    >
                        + Add Role
                    </Button>
                </Box>

                {/* Search Bar */}
                <Box mb={2}>
                    <TextField
                        label='Search roles...'
                        variant='outlined'
                        fullWidth
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Box>

                {/* Roles Table */}
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <strong>S.No</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Name</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Feature</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Permissions</strong>
                                </TableCell>
                                <TableCell align='center'>
                                    <strong>Actions</strong>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        {isLoading && (
                            <TableBody>
                                <TableRow>
                                    <TableCell colSpan={5} align='center'>
                                        Loading roles…
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )}
                        {!isLoading && (
                            <TableBody>
                                {roles.length > 0 ? (
                                    roles.map((role, index) => {
                                        const featureName =
                                            features.find(
                                                (f) => f._id === role.featureId
                                            )?.name || '—';
                                        return (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    {index + 1}
                                                </TableCell>
                                                <TableCell>
                                                    {role.name}
                                                </TableCell>
                                                <TableCell>
                                                    {featureName}
                                                </TableCell>
                                                <TableCell>
                                                    {(role.permissions || [])
                                                        .map((permId) =>
                                                            getPermissionNames([
                                                                permId,
                                                            ])
                                                        )
                                                        .flat()
                                                        .join(', ') || '—'}
                                                </TableCell>
                                                <TableCell align='center'>
                                                    <Button
                                                        variant='outlined'
                                                        color='primary'
                                                        onClick={() =>
                                                            handleEditClick(
                                                                role
                                                            )
                                                        }
                                                    >
                                                        Edit
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} align='center'>
                                            No roles found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        )}
                    </Table>
                </TableContainer>

                {/* Add/Edit Dialog */}
                <Dialog open={showRoleDialog} onClose={handleClose}>
                    <DialogTitle>
                        {!isEditMode ? 'Add New Role' : 'Edit Role'}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            margin='dense'
                            label='Role Name'
                            name='name'
                            value={selectedRole.name || ''}
                            onChange={handleChange}
                            error={!!errors.name}
                            fullWidth
                        />

                        {/* Feature Dropdown (Single Select) */}
                        <FormControl fullWidth margin='dense'>
                            <InputLabel id='feature-label'>Feature</InputLabel>
                            <Select
                                labelId='feature-label'
                                name='featureId'
                                value={selectedRole.featureId || ''}
                                onChange={handleFeatureChange}
                                fullWidth
                            >
                                {features.map((feat) => (
                                    <MenuItem key={feat._id} value={feat._id}>
                                        {feat.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {errors.featureId && (
                            <Typography color='error' variant='body2'>
                                {errors.featureId}
                            </Typography>
                        )}

                        {/* Permissions Multi-select */}
                        <FormControl fullWidth margin='dense'>
                            <InputLabel id='permissions-label'>
                                Permissions
                            </InputLabel>
                            <Select
                                labelId='permissions-label'
                                multiple
                                value={selectedPermissions || []}
                                onChange={handlePermissionChange}
                                input={<OutlinedInput />}
                                renderValue={(selectedValues) => (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        {(selectedValues as string[]).map(
                                            (perm) => (
                                                <Chip key={perm} label={perm} />
                                            )
                                        )}
                                    </Box>
                                )}
                            >
                                {permissions.map((perm) => (
                                    <MenuItem key={perm._id} value={perm.name}>
                                        {perm.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} color='secondary'>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            color='primary'
                            variant='contained'
                        >
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={() =>
                            setSnackbar({ ...snackbar, open: false })
                        }
                        severity={snackbar.severity}
                        variant='filled'
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </>
    );
};

export default RoleList;